// File: frontend/src/pages/parent/ParentDashboard.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSchool } from '../../context/SchoolContext';
import api from '../../utils/axios';

const ParentDashboard = () => {
  const { user, logout } = useAuth();
  const { school } = useSchool();
  const navigate = useNavigate();

  const [child, setChild] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [feeSummary, setFeeSummary] = useState(null);
  const [resultsData, setResultsData] = useState([]);
  const [upcomingExams, setUpcomingExams] = useState([]);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch all parent portal dashboard data asynchronously
   */
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [childRes, attRes, feeRes, resultsRes, examsRes] = await Promise.all([
        api.get('/api/v1/parent/child'),
        api.get('/api/v1/parent/attendance'),
        api.get('/api/v1/parent/fees'),
        api.get('/api/v1/parent/results'),
        api.get('/api/v1/parent/exams')
      ]);

      if (childRes.data?.success) setChild(childRes.data.data);
      if (attRes.data?.success) setAttendanceSummary(attRes.data.data?.summary);
      if (feeRes.data?.success) setFeeSummary(feeRes.data.data?.summary);
      if (resultsRes.data?.success) setResultsData(resultsRes.data.data || []);
      if (examsRes.data?.success) setUpcomingExams(examsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching parent dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const fatherName = child?.father_name || user?.name || 'Parent';
  const latestGrade = resultsData[0]?.overall_grade || 'A';

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {school?.logo ? (
              <img src={school.logo} alt="Logo" className="h-10 w-auto max-w-[140px] object-contain" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white flex items-center justify-center font-bold text-base shadow-sm">
                👨‍👩‍👧
              </div>
            )}
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Parent Portal</h1>
              <p className="text-xs text-slate-500">Student Progress & Academic Dashboard</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex flex-col text-right">
              <span className="text-sm font-semibold text-slate-800">Hello, {fatherName}</span>
              <span className="text-xs text-indigo-600 font-medium">Parent Access</span>
            </div>
            <button
              onClick={handleLogout}
              className="bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-700 font-medium px-4 py-2 rounded-xl text-sm transition border border-slate-200"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Welcome & Child Info Card */}
        <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-blue-800 rounded-2xl p-6 sm:p-8 text-white shadow-xl">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <span className="inline-block px-3 py-1 bg-white/10 rounded-full text-xs font-semibold uppercase tracking-wider text-indigo-200">
                Parent Control Center
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold">
                Hello, {fatherName}!
              </h2>
              <p className="text-indigo-100 text-sm">
                Here is your child's live academic standing, attendance overview, and notices.
              </p>
            </div>

            {/* Child Metadata Card */}
            {child && (
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-xl flex items-center gap-4 max-w-sm w-full">
                <div className="w-14 h-14 rounded-full bg-white text-indigo-900 flex items-center justify-center font-extrabold text-xl shadow-md border-2 border-indigo-200">
                  {child.student_name.slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-bold text-white text-base leading-tight">{child.student_name}</h3>
                  <p className="text-xs text-indigo-200 mt-0.5">
                    Class: <span className="font-bold text-white">{child.class_name || `Class ${child.class_id}`}</span> • Roll No: <span className="font-mono font-bold text-white">{child.roll_no || child.id}</span>
                  </p>
                  <span className="inline-block mt-1 text-[10px] bg-emerald-400/20 text-emerald-300 px-2 py-0.5 rounded font-bold uppercase">
                    Status: {child.status || 'Active'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 4 Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* Card 1: Attendance Rate */}
          <div 
            onClick={() => navigate('/parent/attendance')}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md cursor-pointer transition"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-lg">
                📅
              </div>
              <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">This Month</span>
            </div>
            <p className="text-sm font-medium text-slate-500">Attendance Rate</p>
            <h3 className="text-3xl font-extrabold text-slate-800 mt-1">
              {loading ? '...' : `${attendanceSummary?.attendance_percentage || 100}%`}
            </h3>
          </div>

          {/* Card 2: Fee Status */}
          <div 
            onClick={() => navigate('/parent/fees')}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md cursor-pointer transition"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg">
                💳
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                (feeSummary?.total_pending || 0) <= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
              }`}>
                {(feeSummary?.total_pending || 0) <= 0 ? 'Clear' : 'Pending'}
              </span>
            </div>
            <p className="text-sm font-medium text-slate-500">Fee Balance</p>
            <h3 className="text-3xl font-extrabold text-slate-800 mt-1">
              {loading ? '...' : `$${(feeSummary?.total_pending || 0).toFixed(2)}`}
            </h3>
          </div>

          {/* Card 3: Last Result Grade */}
          <div 
            onClick={() => navigate('/parent/results')}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md cursor-pointer transition"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-lg">
                🎓
              </div>
              <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2.5 py-1 rounded-full">Overall</span>
            </div>
            <p className="text-sm font-medium text-slate-500">Last Result Grade</p>
            <h3 className="text-3xl font-extrabold text-slate-800 mt-1">
              {loading ? '...' : latestGrade}
            </h3>
          </div>

          {/* Card 4: Upcoming Exams */}
          <div 
            onClick={() => navigate('/parent/results')}
            className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md cursor-pointer transition"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold text-lg">
                📝
              </div>
              <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">Scheduled</span>
            </div>
            <p className="text-sm font-medium text-slate-500">Upcoming Exams</p>
            <h3 className="text-3xl font-extrabold text-slate-800 mt-1">
              {loading ? '...' : upcomingExams.length}
            </h3>
          </div>

        </div>

        {/* Quick Action Navigation Buttons */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
          <h3 className="font-bold text-slate-800 text-base mb-4">Quick Portal Access</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            
            <Link
              to="/parent/attendance"
              className="p-4 rounded-xl bg-slate-50 hover:bg-emerald-50 hover:border-emerald-200 border border-slate-200 text-center transition group"
            >
              <div className="text-2xl mb-1">📅</div>
              <span className="font-bold text-slate-800 group-hover:text-emerald-700 text-xs block">View Attendance</span>
            </Link>

            <Link
              to="/parent/fees"
              className="p-4 rounded-xl bg-slate-50 hover:bg-blue-50 hover:border-blue-200 border border-slate-200 text-center transition group"
            >
              <div className="text-2xl mb-1">💳</div>
              <span className="font-bold text-slate-800 group-hover:text-blue-700 text-xs block">Pay / View Fees</span>
            </Link>

            <Link
              to="/parent/results"
              className="p-4 rounded-xl bg-slate-50 hover:bg-purple-50 hover:border-purple-200 border border-slate-200 text-center transition group"
            >
              <div className="text-2xl mb-1">📊</div>
              <span className="font-bold text-slate-800 group-hover:text-purple-700 text-xs block">View Results</span>
            </Link>

            <Link
              to="/parent/notices"
              className="p-4 rounded-xl bg-slate-50 hover:bg-amber-50 hover:border-amber-200 border border-slate-200 text-center transition group"
            >
              <div className="text-2xl mb-1">📢</div>
              <span className="font-bold text-slate-800 group-hover:text-amber-700 text-xs block">School Notices</span>
            </Link>

          </div>
        </div>

      </main>
    </div>
  );
};

export default ParentDashboard;
